import React, { useState, useEffect, useRef } from "react";
import { Table, Input, ColorPicker, Select, Tag } from "antd";
import styles from "./workflow.module.scss";
import KoIcon from '@kwapio/icons';
import KoButton from "packages/button";
import { KoDescription, KoText, KoTitle } from "packages/typography";
import KoTag from "packages/tag";
import { TEMPLATECONTNET } from "./contstant";
import KoImage from "packages/image";
import { KoDate, KoInput } from "packages";

import { useContext, useMemo } from "react";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { HolderOutlined } from "@ant-design/icons";

const RowContext = React.createContext({});

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <span
      ref={setActivatorNodeRef}
      {...listeners}
      style={{ cursor: "move", display: "inline-flex" }}
    >
      <HolderOutlined />
    </span>
  );
};

const DraggableRow = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: props["data-row-key"] });

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {})
  };

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};


const WorkflowEditor = ({ workspace, noend, data, onChange, readOnly, isDefault, resetWorkflow, customAction, customColumns=[]}) => {
  const [workflow, setWorkflow] = useState(data?.workflow || []);
  const tableRef = useRef(null);
  const allStatuses = workflow?.map(({ label, value }) => ({ label, value }));
  let details = TEMPLATECONTNET[data?.templateId] || {};

  useEffect(() => {
    setWorkflow(data?.workflow || []);
  }, [data]);

  const handleLabelChange = (value, newLabel) => {
    const updatedWorkflow = workflow?.map(status => (status.value === value ? { ...status, label: newLabel } : status));
    const filteredActivities = updatedWorkflow.filter(item => item.label && item.label.trim() !== "");
    setWorkflow(filteredActivities);
    let template = data;
    template.workflow = updatedWorkflow;
    onChange && onChange(template);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = workflow.findIndex(i => i.value === active.id);
    const newIndex = workflow.findIndex(i => i.value === over.id);

    const reordered = arrayMove(workflow, oldIndex, newIndex).map(
      (item, index) => ({
        ...item,
        value: index + 1
      })
    );

    setWorkflow(reordered);

    let template = data;
    template.workflow = reordered;
    onChange && onChange(template);
  };


  const handleColorChange = (value, newColor, key) => {
    const updatedWorkflow = workflow?.map(status => {
      if (status.value === value) {
        const newRGBA = `rgba(${newColor.metaColor.r}, ${newColor.metaColor.g}, ${newColor.metaColor.b}, 0.16)`;
        const updatedStatus = { ...status, [key]: newRGBA };
  
        if (key === "background") {
          updatedStatus.color = `rgba(${newColor.metaColor.r}, ${newColor.metaColor.g}, ${newColor.metaColor.b}, .9)`;
        }
  
        return updatedStatus;
      }
      return status;
    });
  
    setWorkflow(updatedWorkflow);
    let template = data;
    template.workflow = updatedWorkflow;
    onChange && onChange(template);
  };

  const handleNextStatusesChange = (value, nextValues) => {
    const updatedWorkflow = workflow?.map(status =>
      status.value === value
        ? { ...status, nextStatuses: allStatuses?.filter(s => nextValues.includes(s.value)) }
        : status
    );
    setWorkflow(updatedWorkflow);
    let template = data;
    template.workflow = updatedWorkflow;
    onChange && onChange(template);
  };

  const handleSelectChange = (rec, value, key) => {
    const updatedWorkflow = workflow?.map(status => (status.value === rec ? { ...status, [key]: value } : status));
    const filteredActivities = updatedWorkflow.filter(item => item.label && item.label.trim() !== "");
    setWorkflow(filteredActivities);
    let template = data;
    template.workflow = updatedWorkflow;
    onChange && onChange(template);
  };


  const handleAddStatus = () => {
    const shouldPreserveLastRow = workspace?.status !== 0;
    const currentWorkflow = [...workflow];

  // Get max value for unique ID generation
    const maxValue = Math.max(...currentWorkflow.map(w => w.value || 0), 0);

    const newStatus = {
      label: "New Status",
      name: "New Status",
      value: maxValue + 1,
      background: "rgba(200, 200, 200, 0.16)",
      color: "#000000",
      nextStatuses: []
    };

    const insertIndex =
    currentWorkflow.length > 1 && shouldPreserveLastRow
      ? currentWorkflow.length - 1
      : currentWorkflow.length;

    const updatedWorkflow = [
      ...currentWorkflow.slice(0, insertIndex),
      newStatus,
      ...currentWorkflow.slice(insertIndex)
    ];

    setWorkflow(updatedWorkflow);

    const template = {
      ...data,
      workflow: updatedWorkflow
    };

    onChange && onChange(template);

    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollTo({
          top: tableRef.current.scrollHeight + 500,
          behavior: "smooth"
        });
      }
    }, 100);
  };



  const handleDeleteStatus = (value) => {
    const shouldPreserveFirstLast = workspace?.status !== 0;

    const currentWorkflow = [...workflow];
    const firstRow = currentWorkflow[0];
    const lastRow = currentWorkflow[currentWorkflow.length - 1];
    const middleRows = currentWorkflow.slice(
      shouldPreserveFirstLast ? 1 : 0,
      shouldPreserveFirstLast ? currentWorkflow.length - 1 : currentWorkflow.length
    ).filter(status => status.value !== value);
    let reindexedMiddle = middleRows.map((status, index) => ({
      ...status,
      value: index + 1
    }));
    const updatedWorkflow = shouldPreserveFirstLast
      ? [firstRow, ...reindexedMiddle, lastRow]
      : reindexedMiddle.map((status, index) => ({ ...status, value: index + 1 }));

    setWorkflow(updatedWorkflow);

    let template = { ...data, workflow: updatedWorkflow };
    onChange && onChange(template);
  };


  const handleTableChange = (newWorkflow) => {
    const updatedWorkflow = newWorkflow.map((status, index) => ({
      ...status,
      value: index + 1 // Update index value based on new order
    }));
    setWorkflow(updatedWorkflow);
    let template = data;
    template.workflow = updatedWorkflow;
    onChange && onChange(template);
  };

  let columns = [
    {
      key: "sort",
      width: 40,
      render: () => <DragHandle />
    },

    {
      title: "#",
      key: "index",
      width: 50,
      fixed: "left",
      render: (text, record, index) => {
        const isFirst = index === 0;
        const isLast = index === workflow.length - 1;

        const tag = isFirst ? "Start" : isLast ? "End" : null;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {tag ? (
              <Tag color={isFirst ? "blue" : "orange"} style={{ marginLeft: 4 }}>
                {index + 1} {tag}
              </Tag>
            ):
              <KoTag iscount={true} value={index + 1} />}
          </div>
        );
      }
    },
    // {
    //   title: "#",
    //   dataIndex: "value",
    //   key: "value",
    //   width: 50,
    //   fixed: "left",
    //   editable: isDefault ? false : true,
    //   render: (text, record) => (
    //     <KoTag iscount={true} value={text} />
    //   )
    // },
    {
      title: "Status",
      dataIndex: "label",
      key: "label",
      width: 200,
      fixed: "left",
      editable: isDefault ? false : true,
      render: (text, record, index) => {

        return (
          <Input
            value={text}
            onChange={(e) => handleLabelChange(record.value, e.target.value)}
          />
        );
      }
    },
    {
      title: "Background",
      dataIndex: "background",
      key: "background",
      align: "center",
      width: 130,
      editable: isDefault ? false : true,
      render: (background, record) => (
        <ColorPicker disabled={isDefault}  defaultFormat="rgba" value={background} onChange={c => handleColorChange(record.value, c, "background")} />
      )
    },
    {
      title: "Preview",
      key: "preview",
      width: 100,
      editable: isDefault ? false : true,
      render: (_, record) => (
        <Tag style={{ background: record.background, color: record.color, padding: "3px 8px" , border:0}}>
          {record.label}
        </Tag>
      )
    },
    {
      title: "Next Statuses",
      dataIndex: "nextStatuses",
      key: "nextStatuses",
      editable: isDefault ? false : true,
      render: (nextStatuses, record) => (
        <Select
          mode="multiple"
          disabled={isDefault}
          style={{ width: "100%" }}
          value={nextStatuses.map(ns => ns.value)}
          showSearch
          onChange={values => handleNextStatusesChange(record.value, values)}
          optionFilterProp="label"
          filterOption={(input, option) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
          options={allStatuses.map(ns => ({
            label: ns.label,
            value: ns.value
          }))}
        >
        </Select>

      )
    },
    // {
    //   title: "Auto Assign",
    //   dataIndex: "autoAssign",
    //   key: "autoAssign",
    //   editable: isDefault ? false : true,
    //   render: (nextStatuses, record) => (
    //     <Select
    //       disabled={isDefault}
    //       style={{ width: "100%" }}
    //       value={nextStatuses.map(ns => ns.value)}
    //       showSearch
    //       onChange={values => handleNextStatusesChange(record.value, values)}
    //       optionFilterProp="label"
    //       filterOption={(input, option) =>
    //         option?.label?.toLowerCase().includes(input.toLowerCase())
    //       }
    //       options={allStatuses.map(ns => ({
    //         label: ns.label,
    //         value: ns.value
    //       }))}
    //     >
    //     </Select>

    //   )
    // },
    {
      title: "",
      key: "actions",
      width: 50,
      fixed: "right",
      align: "right",
      render: (_, record, index) => {
        // const isFirstRow = index === 0;
        // const isLastRow = index === workflow.length - 1;

        // const isDisabled = isDefault || isFirstRow || isLastRow || workspace?.status !== 0;

        return (
          <KoButton
            // disabled={(!noend &&isDisabled)}
            icon={<KoIcon name="DeleteOutlined" />}
            onClick={() => handleDeleteStatus(record.value)}
          />
        );
      }
    }

  ];

  if (customColumns?.length > 0) {
    const updatedColumns = customColumns?.map((column, index) => {
      const { widget } = column;

      const renderWidget = (data, record) => {
        switch (widget) {
        case 'select':
          return (
            <Select
              style={{ width: "100%" }}
              value={data}
              showSearch
              onChange={(value)=>handleSelectChange(record.value, value, column.key)}
              optionFilterProp="label"
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              options={(column.options || []).map(opt => ({
                label: opt.name,
                value: opt.templateId
              }))}
            />
          );

        case 'input':
          return (
            <KoInput
              value={data}
              onChange={e => handleNextStatusesChange(record.value, e.target.value)}
              placeholder={column.placeholder || 'Enter value'}
            />
          );

        case 'date':
          return (
            <KoDate
              style={{ width: '100%' }}
              value={data}
              onChange={date => handleNextStatusesChange(record.value, date)}
            />
          );
        default:
          return <span>{data}</span>;
        }
      };

      return {
        ...column,
        key: index,
        render: renderWidget
      };
    });

    const lastColumn = columns[columns.length - 1];
    const baseColumns = columns.slice(0, -1);
    columns = [...baseColumns, ...updatedColumns, lastColumn];
  }



  return (
    readOnly ? <div className={styles.readOnly}>
      <div className={styles.leftPannel}>
        <div className={styles.header}>
          <KoTitle className={styles.title} text={details?.title} level={2} />
          <KoTitle className={styles.subTitle} text={details?.subTitle} level={5} />
        </div>
        <div className={styles.contentWrapper}>
         
          {
            details?.rows?.map((row, index) => (
              
              <div className={styles.content} key={index}>
                {(index === 0 || index === 2) && <div className={styles.icon}>
                  <KoImage src={`https://raw.githubusercontent.com/kwapio/logo/refs/heads/main/templates/${data.templateId}/${index+1}.svg
`} width={170} height={170}/>
                </div>}
                <div className={styles.description}>
                  <KoDescription text={row?.description} />
                </div>
                {index ===1 && <div className={styles.icon}>
                  <KoImage src={`https://raw.githubusercontent.com/kwapio/logo/refs/heads/main/templates/${data.templateId}/2.svg
`} width={170} height={170}/>
                </div>}
              </div>
            ))
          }
        </div>
      </div>
      <div className={styles.rightPannel}>
        <div className={styles.rightContentWrapper}>
          <div className={styles.rightContent}>
            <div className={styles.workflow}>
              <KoTitle text={"Workflow"} level={2}/>
              <ul>
                {workflow?.map((status, index) => (
                  <li key={index}>
                    <div className={styles.status} style={{background: status.background, color: status.color}}>
                      <KoText text={status.label} style={{color: status.color}}/>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.issueTypes}>
              <KoTitle text={"Activity types"} level={2}/>
              <ul>
                {data?.activityTypes?.map((status, index) => (
                  <li key={index}>
                    <div className={styles.issues}>
                      <KoIcon name={status.icon} width={20} height={20}/>
                      <KoText text={status.label} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {/* <div className={styles.rightFooter}>
          <div className={styles.rightFooterContent}>
            <KoTitle text={"System Features"} level={2}/>
            <ul>
              {details?.systemFeatures?.map((status, index) => (
                <li key={index}>
                  <div className={styles.issues} style={{background: status.background, color: status.color}}>
                    <KoIcon name={status.icon} width={20} height={20}/>
                    <KoText text={status.title} style={{color: status.color}}/>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div> */}
      </div>

    </div> : <div className={`${styles.wrapper}`}>
      {!isDefault && <div className={styles.header}>
        <KoTitle text={data?.name || "Workflow Template"} level={5} />
        <div className={styles.actions}>
          <KoButton label="New Status" type="primary" icon={ <KoIcon color="#FFF" name="PlusOutlined"/>}  onClick={() => handleAddStatus()} />
          {customAction?.length > 0 && customAction.map((action, index) => (
            <KoButton key={index} {...action} />
          ))}
          <KoButton label="Reset Workflow" color="purple" variant="filled" icon={ <KoIcon name="RefreshOutlined"/>}  onClick={() => resetWorkflow()} />
        </div>
      </div>}
      {/* <Table
        dataSource={workflow?.filter(status => status.label.trim() !== "")} // Filter out empty rows
        pagination={false}
        ref={tableRef}
        columns={columns}
        size="small"
        scroll={{
          x: 'max-content',
          y: 'calc(100vh - 40rem)'
        }}
        rowKey="value"
        rowClassName={(_, index) => {
          if (index === 0) return 'workflow-row-start';
          if (index === workflow.length - 1) return 'workflow-row-end';
          return '';
        }}
        onChange={(pagination, filters, sorter, extra) => handleTableChange(extra.currentDataSource)}
      /> */}
      <DndContext
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={workflow.map(i => i.value)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            components={{ body: { row: DraggableRow } }}
            dataSource={workflow?.filter(status => status.label.trim() !== "")}
            pagination={false}
            ref={tableRef}
            columns={columns}
            size="small"
            scroll={{
              x: 'max-content',
              y: 'calc(100vh - 40rem)'
            }}
            rowKey="value"
            rowClassName={(_, index) => {
              if (index === 0) return 'workflow-row-start';
              if (index === workflow.length - 1) return 'workflow-row-end';
              return '';
            }}
            onChange={(pagination, filters, sorter, extra) =>
              handleTableChange(extra.currentDataSource)
            }
          />
        </SortableContext>
      </DndContext>

    </div>
  );
};

export default WorkflowEditor;
